import { useCallback, useEffect, useRef, useState, type UIEvent } from "react"
import { useHistory } from "react-router-dom"

import type { NotificationModel } from "@/components/Notifications/notificationModels"
import {
  notificationsCentreCategoryNotificationPath,
  notificationsCentreCategoryPath,
  notificationsCentreNotificationPath,
  notificationsCentrePath,
  NOTIFICATIONS_INBOX_PAGE_SIZE,
} from "@/constants/notificationsCentre"
import {
  bulkDeleteNotifications,
  deleteNotification,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/services/notificationsApi"
import type { NotificationCategory } from "@/types/notifications"
import { mapNotificationItemToModel, formatNotificationTimeAgo } from "@/utils/notificationInbox"
import { resolveNotificationDisplayTitle } from "@/utils/notificationDisplay"
import {
  buildNotificationStyleSeedItems,
  isStyleSeedNotificationId,
} from "@/utils/notificationStyleSeed"

export type UseNotificationsInboxOptions = {
  enabled: boolean
  selectedNotificationId?: string | null
  categoryFilter?: NotificationCategory | null
}

export type NotificationsInboxState = {
  notifications: NotificationModel[]
  unreadCount: number
  categoryFilter: NotificationCategory | null
  /** True while the active inbox list is refreshing from the API. */
  fetching: boolean
  loadError: boolean
  hasMore: boolean
  isLoadingMore: boolean
  selectedNotification: NotificationModel | null
  reload: () => void
  loadMore: () => void
  markAsRead: (id: string) => void
  markAllRead: () => void
  /** Inject local catalog samples for styling (does not hit the API). */
  seedStyleNotifications: () => number
  remove: (id: string) => void
  /** Soft-delete many notifications (one bulk API call). */
  removeMany: (ids: string[]) => Promise<void>
  /**
   * Soft-delete every notification matching the current inbox filters
   * (category + optional unread-only), including not-yet-loaded pages.
   */
  removeMatching: (options?: { unreadOnly?: boolean }) => Promise<void>
  selectNotification: (notification: NotificationModel) => void
  handleListScroll: (event: UIEvent<HTMLDivElement>) => void
}

function mapWsRowToModel(row: Record<string, unknown>): NotificationModel {
  const createdAtIso = String(row.created_at ?? row.createdAt ?? new Date().toISOString())
  return {
    id: String(row.id),
    type: (row.category as NotificationModel["type"]) ?? "system",
    title: resolveNotificationDisplayTitle(
      String(row.title ?? "Notification"),
      row.event_key != null
        ? String(row.event_key)
        : row.eventKey != null
          ? String(row.eventKey)
          : null,
      (row.data_json ?? row.data) as NotificationModel["details"]
    ),
    message: String(row.body_markdown ?? row.bodyMarkdown ?? ""),
    time: formatNotificationTimeAgo(createdAtIso),
    avatarName: "Tessera",
    avatarSrc: "",
    isRead: String(row.status_in_app ?? row.statusInApp ?? "unread") === "read",
    eventKey:
      row.event_key != null
        ? String(row.event_key)
        : row.eventKey != null
          ? String(row.eventKey)
          : null,
    details: (row.data_json ?? row.data) as NotificationModel["details"],
  }
}

type InboxCacheEntry = {
  notifications: NotificationModel[]
  unreadCount: number
  hasMore: boolean
  cursor: string | null
}

function inboxCacheKey(category: NotificationCategory | null): string {
  return category ?? "__inbox__"
}

export function useNotificationsInboxState({
  enabled,
  selectedNotificationId = null,
  categoryFilter = null,
}: UseNotificationsInboxOptions): NotificationsInboxState {
  const history = useHistory()
  const [notifications, setNotifications] = useState<NotificationModel[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [fetching, setFetching] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [reloadTrigger, setReloadTrigger] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const paginationCursorRef = useRef<string | null>(null)
  const hasLoadedOnceRef = useRef(false)
  const inboxCacheRef = useRef(new Map<string, InboxCacheEntry>())

  const applyInboxCacheEntry = useCallback((entry: InboxCacheEntry) => {
    setNotifications(entry.notifications)
    setUnreadCount(entry.unreadCount)
    setHasMore(entry.hasMore)
    paginationCursorRef.current = entry.cursor
  }, [])

  const syncInboxCache = useCallback(
    (
      category: NotificationCategory | null,
      entry: InboxCacheEntry
    ) => {
      inboxCacheRef.current.set(inboxCacheKey(category), entry)
    },
    []
  )

  const reload = useCallback(() => {
    inboxCacheRef.current.delete(inboxCacheKey(categoryFilter))
    setReloadTrigger((t) => t + 1)
  }, [categoryFilter])

  useEffect(() => {
    if (!enabled) return

    let isMounted = true
    const key = inboxCacheKey(categoryFilter)
    const cached = inboxCacheRef.current.get(key)

    if (cached) {
      applyInboxCacheEntry(cached)
    } else if (hasLoadedOnceRef.current) {
      setNotifications([])
      setUnreadCount(0)
      setHasMore(false)
      paginationCursorRef.current = null
    }

    setFetching(true)
    setLoadError(false)

    void (async () => {
      try {
        const { notifications: items, nextCursor, unreadCount: uc, hasMore: more } =
          await fetchNotifications({
            limit: NOTIFICATIONS_INBOX_PAGE_SIZE,
            ...(categoryFilter ? { category: categoryFilter } : {}),
          })
        if (!isMounted) return

        const mapped = items.map(mapNotificationItemToModel)
        const cursor = nextCursor ?? null
        const entry: InboxCacheEntry = {
          notifications: mapped,
          unreadCount: typeof uc === "number" ? uc : 0,
          hasMore: more ?? Boolean(cursor),
          cursor,
        }
        syncInboxCache(categoryFilter, entry)
        applyInboxCacheEntry(entry)
      } catch (error) {
        console.error("Failed to load notifications:", error)
        if (!isMounted) return
        if (!cached) {
          setNotifications([])
          setUnreadCount(0)
          setHasMore(false)
          paginationCursorRef.current = null
        }
        setLoadError(true)
      } finally {
        if (isMounted) {
          hasLoadedOnceRef.current = true
          setFetching(false)
        }
      }
    })()

    return () => {
      isMounted = false
    }
  }, [applyInboxCacheEntry, categoryFilter, enabled, reloadTrigger, syncInboxCache])

  useEffect(() => {
    if (!enabled || fetching) return
    syncInboxCache(categoryFilter, {
      notifications,
      unreadCount,
      hasMore,
      cursor: paginationCursorRef.current,
    })
  }, [
    categoryFilter,
    enabled,
    fetching,
    hasMore,
    notifications,
    syncInboxCache,
    unreadCount,
  ])

  const loadMore = useCallback(async () => {
    if (!enabled || isLoadingMore || !hasMore) return
    const cursor = paginationCursorRef.current
    if (!cursor) return

    setIsLoadingMore(true)
    try {
      const { notifications: items, nextCursor, hasMore: more } = await fetchNotifications({
        limit: NOTIFICATIONS_INBOX_PAGE_SIZE,
        cursor,
        ...(categoryFilter ? { category: categoryFilter } : {}),
      })
      const mapped = items.map(mapNotificationItemToModel)
      setNotifications((prev) => {
        const seen = new Set(prev.map((x) => x.id))
        const added = mapped.filter((x) => !seen.has(x.id))
        if (added.length === 0) {
          setHasMore(false)
          return prev
        }
        return prev.concat(added)
      })
      const cursorNext = nextCursor ?? null
      paginationCursorRef.current = cursorNext
      const pageHasMore = more ?? Boolean(cursorNext)
      setHasMore(pageHasMore && items.length > 0)
    } catch (error) {
      console.error("Failed to load more notifications:", error)
    } finally {
      setIsLoadingMore(false)
    }
  }, [categoryFilter, enabled, hasMore, isLoadingMore])

  const handleListScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
      if (remaining > 300) return
      void loadMore()
    },
    [loadMore]
  )

  useEffect(() => {
    if (!enabled) return

    const onWsNotification = (evt: Event) => {
      const detail = (evt as CustomEvent).detail as { row?: Record<string, unknown> }
      const row = detail?.row
      if (!row?.id) return

      const next = mapWsRowToModel(row)
      setNotifications((prev) => {
        const without = prev.filter((n) => n.id !== next.id)
        return [next, ...without]
      })
      if (!next.isRead) {
        setUnreadCount((c) => c + 1)
      }
    }

    window.addEventListener("notifications:ws", onWsNotification as EventListener)
    return () =>
      window.removeEventListener("notifications:ws", onWsNotification as EventListener)
  }, [enabled])

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    )
    setUnreadCount((c) => Math.max(0, c - 1))
    if (isStyleSeedNotificationId(id)) return
    markNotificationRead(id).catch((error) => {
      console.error("Failed to mark notification as read:", error)
    })
  }, [])

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
    setUnreadCount(0)
    const hasReal = notifications.some((n) => !isStyleSeedNotificationId(n.id))
    if (!hasReal) return
    void markAllNotificationsRead().catch((error) => {
      console.error("Failed to mark all notifications read:", error)
      notifications.forEach((n) => {
        if (!n.isRead && !isStyleSeedNotificationId(n.id)) {
          markNotificationRead(n.id).catch(() => null)
        }
      })
    })
  }, [notifications])

  const seedStyleNotifications = useCallback((): number => {
    const seeded = buildNotificationStyleSeedItems().map(mapNotificationItemToModel)
    setNotifications((prev) => {
      const withoutSeeds = prev.filter((n) => !isStyleSeedNotificationId(n.id))
      return [...seeded, ...withoutSeeds]
    })
    setUnreadCount((c) => {
      // Recompute from next state is awkward; count unread seeds + prior non-seed unreads.
      return seeded.filter((n) => !n.isRead).length +
        notifications.filter((n) => !isStyleSeedNotificationId(n.id) && !n.isRead).length
    })
    return seeded.length
  }, [notifications])

  const navigateAfterRemoval = useCallback(
    (remaining: NotificationModel[], removedSelected: boolean) => {
      if (!removedSelected) return
      const next = remaining[0]
      if (categoryFilter) {
        history.push(
          next
            ? notificationsCentreCategoryNotificationPath(categoryFilter, next.id)
            : notificationsCentreCategoryPath(categoryFilter)
        )
      } else {
        history.push(
          next ? notificationsCentreNotificationPath(next.id) : notificationsCentrePath()
        )
      }
    },
    [categoryFilter, history]
  )

  const remove = useCallback(
    (id: string) => {
      setNotifications((prev) => {
        const target = prev.find((n) => n.id === id)
        if (target && !target.isRead) {
          setUnreadCount((c) => Math.max(0, c - 1))
        }
        const remaining = prev.filter((n) => n.id !== id)
        navigateAfterRemoval(remaining, selectedNotificationId === id)
        return remaining
      })

      if (!isStyleSeedNotificationId(id)) {
        deleteNotification(id).catch((error) => {
          console.error("Failed to delete notification:", error)
        })
      }
    },
    [navigateAfterRemoval, selectedNotificationId]
  )

  const removeMany = useCallback(
    async (ids: string[]) => {
      const uniqueIds = Array.from(new Set(ids.filter(Boolean)))
      if (uniqueIds.length === 0) return

      const idSet = new Set(uniqueIds)
      const realIds = uniqueIds.filter((id) => !isStyleSeedNotificationId(id))
      const seedIds = uniqueIds.filter((id) => isStyleSeedNotificationId(id))

      setNotifications((prev) => {
        const removedUnread = prev.filter((n) => idSet.has(n.id) && !n.isRead).length
        if (removedUnread > 0) {
          setUnreadCount((c) => Math.max(0, c - removedUnread))
        }
        const remaining = prev.filter((n) => !idSet.has(n.id))
        navigateAfterRemoval(
          remaining,
          selectedNotificationId != null && idSet.has(selectedNotificationId)
        )
        return remaining
      })

      // Style seeds are local-only; still bulk-delete real rows in one request.
      if (realIds.length === 0) return

      try {
        // Backend caps at 500 ids per request; chunk if needed.
        const chunkSize = 500
        for (let i = 0; i < realIds.length; i += chunkSize) {
          const chunk = realIds.slice(i, i + chunkSize)
          await bulkDeleteNotifications({ ids: chunk })
        }
      } catch (error) {
        console.error("Failed to bulk-delete notifications:", error)
        // Seeds were already removed locally; real deletes failed — reload to resync.
        if (seedIds.length === 0) {
          inboxCacheRef.current.delete(inboxCacheKey(categoryFilter))
          setReloadTrigger((t) => t + 1)
        }
      }
    },
    [categoryFilter, navigateAfterRemoval, selectedNotificationId]
  )

  const removeMatching = useCallback(
    async (options?: { unreadOnly?: boolean }) => {
      const unreadOnly = Boolean(options?.unreadOnly)

      setNotifications((prev) => {
        const next = prev.filter((n) => {
          // Keep rows outside the active category filter (should not appear, but be safe).
          if (categoryFilter && n.type !== categoryFilter) return true
          if (unreadOnly && n.isRead) return true
          return false
        })

        const removedUnreadCount = prev.filter((n) => {
          if (next.some((x) => x.id === n.id)) return false
          return !n.isRead
        }).length
        if (removedUnreadCount > 0) {
          setUnreadCount((c) => Math.max(0, c - removedUnreadCount))
        }

        navigateAfterRemoval(
          next,
          selectedNotificationId != null &&
            !next.some((n) => n.id === selectedNotificationId)
        )
        return next
      })

      try {
        await bulkDeleteNotifications({
          allMatching: true,
          category: categoryFilter,
          unreadOnly,
        })
      } catch (error) {
        console.error("Failed to delete matching notifications:", error)
      } finally {
        inboxCacheRef.current.delete(inboxCacheKey(categoryFilter))
        setReloadTrigger((t) => t + 1)
      }
    },
    [categoryFilter, navigateAfterRemoval, selectedNotificationId]
  )

  const selectNotification = useCallback(
    (notification: NotificationModel) => {
      const eventKey = notification.eventKey ?? ""
      const details = notification.details ?? null
      const isAutoTrade =
        eventKey === "automated_strategy.trade_executed" ||
        eventKey === "automated_strategy.trade_closed"

      if (!notification.isRead) {
        markAsRead(notification.id)
      }

      if (isAutoTrade && details && typeof details === "object") {
        const d = details as Record<string, unknown>
        const actionUrl =
          typeof d.action_url === "string" && d.action_url.trim()
            ? d.action_url.trim()
            : null
        const tradeLogUrl =
          typeof d.trade_log_url === "string" && d.trade_log_url.trim()
            ? d.trade_log_url.trim()
            : null
        const tradeId =
          typeof d.trade_id === "string" && d.trade_id.trim()
            ? d.trade_id.trim()
            : null

        const candidate =
          actionUrl ||
          tradeLogUrl ||
          (tradeId ? `/trade-log?tradeId=${encodeURIComponent(tradeId)}` : null)
        if (candidate) {
          let path = candidate
          try {
            if (/^https?:\/\//i.test(candidate)) {
              const u = new URL(candidate)
              path = `${u.pathname}${u.search}${u.hash || ""}`
            }
          } catch {
            // keep candidate as-is
          }
          history.push(path)
          return
        }
      }

      history.push(
        categoryFilter
          ? notificationsCentreCategoryNotificationPath(categoryFilter, notification.id)
          : notificationsCentreNotificationPath(notification.id)
      )
    },
    [categoryFilter, history, markAsRead]
  )

  const selectedNotification =
    selectedNotificationId != null
      ? notifications.find((n) => n.id === selectedNotificationId) ?? null
      : null

  return {
    notifications,
    unreadCount,
    categoryFilter,
    fetching,
    loadError,
    hasMore,
    isLoadingMore,
    selectedNotification,
    reload,
    loadMore,
    markAsRead,
    markAllRead,
    seedStyleNotifications,
    remove,
    removeMany,
    removeMatching,
    selectNotification,
    handleListScroll,
  }
}
